// @flow
import { FIELD_SETS, FIRST_SECTION_LABEL_KEYS } from '../constants';
import { toAtomicProgramNames } from '../../../utils/affordable';

export type FieldSet = $Values<typeof FIELD_SETS>;

export type FieldVisibility = {
  showHouseholdIncome: boolean,
  showSubmissionStamp: boolean,
  showDemographics: boolean,
  showMailingAddress: boolean,
  showAccommodation: boolean,
  showDisplacement: boolean,
  showContactInformation: boolean,
  showPreferences: boolean,
  noPreferenceOptionEnabled: boolean,
};

// Gates the Selected Program(s) table, which stays keyed on the property rather
// than on the resolved field set: it is the only control that ticks programs,
// so a mixed property that reaches CONV by ticking keeps it as the route back.
export const isConventionalOnly = (
  propertyAffordablePrograms: ?$ReadOnlyArray<string>,
): boolean => !propertyAffordablePrograms?.length;

export const resolveFieldSet = ({
  affordableProgramNames,
  waitlistEnabled,
  selectedPrograms,
  hasConventionalUnits,
  isWaitlistConfigurationLoaded,
}: {
  affordableProgramNames: ?$ReadOnlyArray<?string>,
  waitlistEnabled: boolean,
  selectedPrograms: ?Array<string>,
  hasConventionalUnits: boolean,
  isWaitlistConfigurationLoaded: boolean,
}): FieldSet => {
  const affordable = new Set((affordableProgramNames ?? []).filter(Boolean));

  if (affordable.size === 0) {
    return FIELD_SETS.CONV;
  }

  // Income, accommodation and displacement only feed the waitlist row, so a
  // property with market-rate units and its waitlist off asks for none of them
  // whatever the agent ticks. Unknown is not off: before the configuration
  // loads this would unregister the contact fields the prospect seeded.
  // Extending this to affordable-only properties is dropping
  // `hasConventionalUnits`.
  if (
    isWaitlistConfigurationLoaded &&
    !waitlistEnabled &&
    hasConventionalUnits
  ) {
    return FIELD_SETS.CONV;
  }

  // A selection saved before the table listed programs individually holds the
  // layered name whole (`LIHTC/HOME`), which matches nothing in a property list
  // that is now individual. Splitting both sides is what keeps those households
  // on their affordable field set no matter which repo ships first.
  const ticked = toAtomicProgramNames(selectedPrograms);

  // Conventional is the only row in the Selected Program(s) table that is not
  // one of the property's affordable programs, so a selection holding no
  // affordable program is a market-rate one. Whether the waitlist covers the
  // ticked program is deliberately not asked: the waitlist picker decides who
  // must queue, not which fields the household is asked for, and a layered
  // program left off it is still affordable.
  const selectionIsConventional =
    ticked.length > 0 && !ticked.some((program) => affordable.has(program));

  if (selectionIsConventional) {
    return FIELD_SETS.CONV;
  }

  return waitlistEnabled ? FIELD_SETS.WL : FIELD_SETS.AFF;
};

export const getFieldVisibility = (
  fieldSet: FieldSet,
  { usePreferencesForRanking }: { usePreferencesForRanking: boolean },
): FieldVisibility => {
  const isWaitlist = fieldSet === FIELD_SETS.WL;
  const isConventional = fieldSet === FIELD_SETS.CONV;

  return {
    showHouseholdIncome: !isConventional,
    showSubmissionStamp: isWaitlist,
    showDemographics: isWaitlist,
    showMailingAddress: isWaitlist,
    showAccommodation: !isConventional,
    showDisplacement: !isConventional,
    showContactInformation: !isConventional,
    showPreferences: isWaitlist && Boolean(usePreferencesForRanking),
    // A waitlist position is written per selected bedroom, and "No Preference"
    // names no bedroom, so the option is only offered where nothing is
    // positioned.
    noPreferenceOptionEnabled: !isWaitlist,
  };
};

export const getFirstSectionLabelKey = (fieldSet: FieldSet): string =>
  fieldSet === FIELD_SETS.CONV
    ? FIRST_SECTION_LABEL_KEYS.HOUSEHOLD
    : FIRST_SECTION_LABEL_KEYS.INCOME;
